import React, { useState } from 'react'
import {
  CheckCircle2,
  FileSearch,
  LoaderCircle,
  Play,
} from 'lucide-react'
import { api, getApiErrorMessage } from '../../lib/api'
import { unwrapAsyncResult } from '../../lib/asyncOperation'

const FOCUS_OPTIONS = [
  ['DISCOVERY', '고객 상황 확인'],
  ['ACCURACY', '정확한 안내'],
  ['EXPLANATION', '쉽게 설명하기'],
  ['EMPATHY', '공감 표현'],
  ['COMPLIANCE', '준법과 고지'],
  ['CLOSING', '다음 행동 정리'],
  ['DIALOGUE_BALANCE', '간결하고 균형 있게 말하기'],
]

export default function CounselPracticeDerivedAnalysis({ onPracticeContent }) {
  const [summary, setSummary] = useState('')
  const [insuranceTypeLabel, setInsuranceTypeLabel] = useState('')
  const [focusCodes, setFocusCodes] = useState(['DISCOVERY', 'EXPLANATION'])
  const [noticeConfirmed, setNoticeConfirmed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')

  const toggleFocus = (code) => {
    setFocusCodes((current) => current.includes(code)
      ? current.filter((item) => item !== code)
      : [...current, code])
  }

  const submit = async (event) => {
    event.preventDefault()
    if (!summary.trim()) {
      setError('상담 내용을 간단히 요약해 주세요.')
      return
    }
    if (!noticeConfirmed) {
      setError('고객에게 상담 분석 목적을 고지한 뒤 확인해 주세요.')
      return
    }

    setSubmitting(true)
    setResult(null)
    setError('')
    try {
      const response = await api.post('/counsel-practice/analysis-jobs', {
        summary: summary.trim(),
        insuranceTypeLabel: insuranceTypeLabel.trim(),
        focusCodes,
        noticeConfirmed,
      }, { __silent: true })
      setResult(await unwrapAsyncResult(response, { intervalMs: 250, timeoutMs: 30000 }))
    } catch (e) {
      setError(getApiErrorMessage(e, e?.message || '상담 분석을 마치지 못했습니다.'))
    } finally {
      setSubmitting(false)
    }
  }

  const recommendations = Array.isArray(result?.recommendations) ? result.recommendations : []
  const focusAreas = Array.isArray(result?.focusAreas) ? result.focusAreas : []

  return (
    <section className="mt-7 border-t border-slate-200 pt-5" aria-labelledby="derived-analysis-title" data-testid="counsel-derived-analysis">
      <div className="flex items-center gap-2">
        <FileSearch className="h-5 w-5 text-indigo-700" aria-hidden="true" />
        <h3 id="derived-analysis-title" className="text-base font-semibold text-slate-950">실제 상담에 맞춘 다음 연습</h3>
      </div>
      <p className="mt-2 text-sm leading-6 text-slate-600">식별정보를 뺀 상담 요약으로 지금 필요한 연습을 찾아보세요.</p>

      <form className="mt-4 grid gap-5 lg:grid-cols-[minmax(0,1fr)_19rem]" onSubmit={submit}>
        <div className="space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">비식별 상담 요약</span>
            <textarea
              value={summary}
              onChange={(event) => setSummary(event.target.value)}
              maxLength={3000}
              rows={6}
              className="mt-1.5 w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm leading-6 text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              placeholder="고객의 질문, 상담 중 어려웠던 점, 다음에 보완하고 싶은 내용을 요약해 주세요."
              aria-describedby="derived-summary-privacy"
            />
          </label>
          <p id="derived-summary-privacy" className="text-xs text-slate-500">이름·연락처 등 식별정보와 상담 원음은 입력하거나 저장하지 않습니다.</p>

          <label className="block max-w-md">
            <span className="text-sm font-medium text-slate-700">관련 보험 종류</span>
            <input
              value={insuranceTypeLabel}
              onChange={(event) => setInsuranceTypeLabel(event.target.value)}
              maxLength={100}
              className="mt-1.5 h-10 w-full rounded-md border border-slate-300 px-3 text-sm text-slate-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
              placeholder="예: 실손의료보험"
            />
          </label>
        </div>

        <div>
          <fieldset>
            <legend className="text-sm font-medium text-slate-700">다시 연습할 부분</legend>
            <div className="mt-2 space-y-1">
              {FOCUS_OPTIONS.map(([code, label]) => (
                <label key={code} className="flex min-h-9 cursor-pointer items-center gap-2 rounded-md px-2 text-sm text-slate-700 hover:bg-slate-50">
                  <input type="checkbox" checked={focusCodes.includes(code)} onChange={() => toggleFocus(code)} className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <label className="mt-4 flex cursor-pointer items-start gap-2 border-t border-slate-200 pt-4 text-sm leading-6 text-slate-700">
            <input type="checkbox" checked={noticeConfirmed} onChange={(event) => setNoticeConfirmed(event.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
            <span>고객에게 상담 분석 목적을 고지했습니다.</span>
          </label>

          <button type="submit" disabled={submitting} className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-sky-600 px-4 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50">
            {submitting ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" /> : <FileSearch className="h-4 w-4" aria-hidden="true" />}
            {submitting ? '연습 찾는 중' : '맞춤 연습 찾기'}
          </button>
        </div>
      </form>

      {error && <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">{error}</div>}

      {result && (
        <div className="mt-6 border-t border-slate-200 pt-5" data-testid="derived-analysis-result">
          <div className="flex flex-wrap items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-700" aria-hidden="true" />
            <h4 className="font-semibold text-slate-950">추천 연습을 찾았습니다</h4>
            <span className="text-xs text-slate-500">상담 원음 저장 안 함</span>
          </div>
          {result.nextAction && <p className="mt-2 text-sm leading-6 text-slate-600">{result.nextAction}</p>}
          {focusAreas.length > 0 && <p className="mt-2 text-xs text-slate-500">집중할 부분: {focusAreas.map((item) => item.label).join(' · ')}</p>}
          <div className="mt-4 divide-y divide-slate-200 border-y border-slate-200">
            {recommendations.map((item) => (
              <div key={`${item.contentType}-${item.contentCode}`} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900">{item.title}</div>
                  <div className="mt-1 text-xs text-slate-500">{item.contentTypeLabel} · {item.insuranceTypeLabel}</div>
                  {item.recommendedPersonaLabel && <div className="mt-1 text-xs font-medium text-indigo-700">{item.recommendedPersonaLabel} · {item.recommendedDifficultyLabel}</div>}
                  <p className="mt-2 text-sm leading-6 text-slate-600">{item.why}</p>
                </div>
                <button type="button" onClick={() => onPracticeContent?.(item.contentType, item.contentCode, item.recommendedPersona, item.recommendedDifficulty, result.analysisKey)} className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-md bg-slate-900 px-3 text-sm font-semibold text-white hover:bg-slate-800">
                  <Play className="h-4 w-4" aria-hidden="true" />연습 시작
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}
